import { Alert, Box, Chip, Collapse, Stack, TextField, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import React, { useState } from 'react';
import { MonospaceBlock } from './MonospaceBlock.styles';
import { MutedPanel } from 'src/UIKit';
import { TextHighlight } from './TextHighlight.styles';
import { useBlocklistApi } from '../../api';
import { BlocklistValidateResponse } from '../../types';

interface BlocklistValidateFormProps {
  testText?: string;
  onTestTextChange?: (text: string) => void;
  disabled?: boolean;
}

export const BlocklistValidateForm: React.FC<BlocklistValidateFormProps> = ({
  testText = '',
  onTestTextChange,
  disabled = false,
}) => {
  const [inputTestText, setInputTestText] = useState(testText);
  const [isLoading, setIsLoading] = useState(false);
  const [testResult, setTestResult] = useState<BlocklistValidateResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showResults, setShowResults] = useState(false);

  const api = useBlocklistApi();

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setInputTestText(value);
    onTestTextChange?.(value);

    // Clear error and results when user starts typing
    if (error) {
      setError(null);
    }
    if (testResult) {
      setTestResult(null);
      setShowResults(false);
    }
  };

  const handleTest = async () => {
    if (!inputTestText.trim()) {
      setError('Please enter some text to test.');
      setTestResult(null);
      setShowResults(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      await api.validate({ texts: [inputTestText.trim()] });

      setTestResult({
        hasMatches: false,
        matches: [],
        message: 'No blocklist matches found.',
      });
      setShowResults(true);
    } catch (err) {
      if (err?.code === 422 || err?.error === 'blocklist_match') {
        if (err?.matches && Array.isArray(err.matches)) {
          const allWords = err.matches.flat();
          const uniqueWords = [...new Set(allWords)];

          const formattedMatches = uniqueWords.map((word: string) => ({
            word,
          }));

          setTestResult({
            hasMatches: true,
            matches: formattedMatches,
            message: err.message || 'Blocklist matches found',
          });
          setShowResults(true);
          setError(null);
        } else {
          setError(err?.message || 'Validation failed');
          setTestResult(null);
          setShowResults(false);
        }
      } else if (err?.code === 400) {
        const errorMessage = err?.message || 'Bad request - please check your input';
        setError(errorMessage);
        setTestResult(null);
        setShowResults(false);
      } else if (err?.code >= 500) {
        setError('Server error occurred. Please try again later.');
        setTestResult(null);
        setShowResults(false);
      } else {
        const errorMessage =
          err?.message || 'Failed to test text against blocklist. Please try again.';
        setError(errorMessage);
        setTestResult(null);
        setShowResults(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const renderHighlightedText = (text: string, matches: BlocklistValidateResponse['matches']) => {
    if (!matches || !matches.length) {
      return text;
    }

    const parts: Array<{ text: string; isMatch: boolean }> = [];

    const matchWords = matches.map(match => match.word);
    const pattern = new RegExp(
      `\\b(${matchWords.map(word => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})\\b`,
      'gi',
    );

    let lastIndex = 0;
    let match;

    while ((match = pattern.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push({ text: text.slice(lastIndex, match.index), isMatch: false });
      }

      parts.push({ text: match[0], isMatch: true });
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
      parts.push({ text: text.slice(lastIndex), isMatch: false });
    }

    return (
      <span>
        {parts.map((part, index) =>
          part.isMatch ? (
            <TextHighlight key={index}>{part.text}</TextHighlight>
          ) : (
            <span key={index}>{part.text}</span>
          ),
        )}
      </span>
    );
  };

  return (
    <Box mt={2}>
      <TextField
        fullWidth
        multiline
        rows={3}
        label="Text to validate"
        placeholder="Enter text to test against the blocklist..."
        value={inputTestText}
        onChange={handleInputChange}
        disabled={disabled || isLoading}
        margin="dense"
        error={!!error}
        helperText={error}
      />

      <Stack direction="row" justifyContent="flex-end" mt={2}>
        <LoadingButton
          variant="contained"
          color="secondary"
          size="large"
          onClick={handleTest}
          loading={isLoading}
          disabled={disabled || !inputTestText.trim()}
        >
          Validate
        </LoadingButton>
      </Stack>

      <Collapse in={showResults}>
        {testResult && (
          <Box mt={2}>
            <Alert severity={testResult.hasMatches ? 'warning' : 'success'}>
              {testResult.hasMatches ? (
                <Box>
                  <Typography variant="body2" gutterBottom>
                    <strong>
                      {testResult.matches && testResult.matches.length === 1
                        ? '1 blocklist match found!'
                        : `${testResult.matches?.length || 0} blocklist matches found!`}
                    </strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    The following blocked words/phrases were detected:
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={0.5} mb={1}>
                    {testResult.matches?.map((match, index) => (
                      <Chip
                        key={index}
                        label={`"${match.word}"`}
                        color="error"
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                </Box>
              ) : (
                <Box>
                  <Typography variant="body2" gutterBottom>
                    <strong>No blocklist matches found.</strong> This text is clear to use.
                  </Typography>
                </Box>
              )}
            </Alert>

            {testResult.hasMatches && testResult.matches && testResult.matches.length > 0 && (
              <MutedPanel mt={1}>
                <Typography variant="subtitle2" gutterBottom>
                  🔍 Text with highlighted matches:
                </Typography>
                <MonospaceBlock>
                  {renderHighlightedText(inputTestText, testResult.matches)}
                </MonospaceBlock>
              </MutedPanel>
            )}
          </Box>
        )}
      </Collapse>
    </Box>
  );
};
